// Client-side SePay QR generation.
//
// The Worker only exposes the PUBLIC SePay config (no secrets) and the webhook.
// QR images are built here in the browser so no secret ever reaches the client.
//
// Reference format must stay in sync with the webhook parser:
//   cloudflare/ocri-worker/src/routes/webhook.ts  ->  /^OCR-(free|plus|pro)-([A-F0-9-]+)$/i
// The second segment is looked up as a USER ID, so we put the user's UUID there.

import { api } from '@/lib/api';

export interface SepayQr {
  qr_url: string;
  reference: string;
  amount: number;
  expires_at: number;
}

/** How long a payment reference stays valid (matches the app's 15-minute window). */
const QR_TTL_MS = 15 * 60 * 1000;

export type BillingCycle = 'monthly' | 'quarterly' | 'yearly';

export const CYCLE_MONTHS: Record<BillingCycle, number> = {
  monthly: 1,
  quarterly: 3,
  yearly: 12,
};

/** Discount per cycle, mirrors the Worker's voucher pricing table. */
export const CYCLE_DISCOUNT: Record<BillingCycle, number> = {
  monthly: 0,
  quarterly: 10,
  yearly: 16,
};

export function calcAmount(priceMonthly: number, cycle: BillingCycle): number {
  const months = CYCLE_MONTHS[cycle];
  const discount = CYCLE_DISCOUNT[cycle];
  return Math.round(priceMonthly * months * (1 - discount / 100));
}

export function buildReference(planId: string, userId: string): string {
  return `OCR-${planId.toUpperCase()}-${userId}`;
}

/**
 * Build a SePay-hosted QR image URL for a bank transfer.
 * Docs: https://docs.sepay.vn/tich-hop-thanh-toan.html
 */
export function buildQrUrl(params: {
  bankCode: string;
  accountNumber: string;
  template: string;
  amount: number;
  description: string;
}): string {
  const query = new URLSearchParams({
    acc: params.accountNumber,
    bank: params.bankCode,
    amount: String(params.amount),
    des: params.description,
    template: params.template || 'compact',
  });
  return `https://qr.sepay.vn/img?${query.toString()}`;
}

/**
 * Create a payment QR for the given plan. Requires the user to be logged in —
 * the user id is embedded in the reference so the webhook can credit them.
 */
export async function createSepayQr(
  planId: string,
  userId: string,
  priceMonthly: number,
  cycle: BillingCycle = 'monthly'
): Promise<SepayQr> {
  const config = await api.getSepayPublicConfig();

  if (!config.account_number || !config.bank_code) {
    throw new Error('SePay chưa được cấu hình. Vui lòng liên hệ quản trị viên.');
  }

  const amount = calcAmount(priceMonthly, cycle);
  if (amount <= 0) {
    throw new Error('Gói này không cần thanh toán.');
  }

  const reference = buildReference(planId, userId);

  return {
    qr_url: buildQrUrl({
      bankCode: config.bank_code,
      accountNumber: config.account_number,
      template: config.template,
      amount,
      description: reference,
    }),
    reference,
    amount,
    expires_at: Date.now() + QR_TTL_MS,
  };
}